import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  GlobalOutlined,
  LockOutlined,
  MoreOutlined,
  SettingOutlined,
  ShareAltOutlined,
  StarFilled,
  StarOutlined,
  TeamOutlined,
  UserSwitchOutlined
} from "@ant-design/icons"
import {
  Button,
  Dropdown,
  type MenuProps,
  type TableProps,
  Tag,
  Tooltip,
  Typography
} from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../components/EntityTable/surfaces.ts"
import { brand, neutral, semantic } from "../../../components/widgets/tokens.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import type {
  ReportAccessLevel,
  ReportListItem,
  ReportVisibility
} from "../types/types.ts"

const { Text } = Typography

const VISIBILITY_ICON: Record<ReportVisibility, React.ReactNode> = {
  private: <LockOutlined />,
  team: <TeamOutlined />,
  role: <UserSwitchOutlined />,
  company: <GlobalOutlined />
}

const ACCESS_COLOR: Record<ReportAccessLevel, string> = {
  owner: "green",
  edit: "blue",
  view: "default"
}

const accessLevelOf = (report: ReportListItem): ReportAccessLevel =>
  report.access_level ?? "owner"

// Shown only after switching them on in Table settings; the sharing cell and
// the row menu already say what they decide.
const DEFAULT_HIDDEN_COLUMNS = ["access", "widget_count"] as const

const byText = (a: string, b: string) => a.localeCompare(b)

export interface ReportsListActions {
  onOpen: (report: ReportListItem) => void
  onEdit: (report: ReportListItem) => void
  onToggleFavorite: (report: ReportListItem) => void
  onSettings: (report: ReportListItem) => void
  onMove: (report: ReportListItem) => void
  onShare: (report: ReportListItem) => void
  onDuplicate: (report: ReportListItem) => void
  onDelete: (report: ReportListItem) => void
}

interface ReportsListProps extends ReportsListActions {
  reports: ReportListItem[]
  loading: boolean
  /** Off inside one folder, where every row would repeat its name. */
  showFolder: boolean
  rowSelection: TableProps<ReportListItem>["rowSelection"]
  configControl: { open: boolean; onOpenChange: (open: boolean) => void }
}

export const ReportsList = ({
  reports,
  loading,
  showFolder,
  rowSelection,
  configControl,
  onOpen,
  onEdit,
  onToggleFavorite,
  onSettings,
  onMove,
  onShare,
  onDuplicate,
  onDelete
}: ReportsListProps) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()

  const rowMenu = useMemo(
    () =>
      (record: ReportListItem): MenuProps["items"] => {
        const owned = accessLevelOf(record) === "owner"
        // Settings and filing write the columns PATCH allows an editor to
        // write; sharing and deletion stay with the owner.
        const editable = accessLevelOf(record) !== "view"
        return [
          {
            key: "open",
            icon: <EyeOutlined />,
            label: t("reports.actions.open"),
            onClick: () => onOpen(record)
          },
          ...(editable
            ? [
                {
                  key: "edit",
                  icon: <EditOutlined />,
                  label: t("reports.actions.edit"),
                  onClick: () => onEdit(record)
                },
                {
                  key: "settings",
                  icon: <SettingOutlined />,
                  label: t("reports.actions.settings"),
                  onClick: () => onSettings(record)
                },
                {
                  key: "move",
                  icon: <FolderOpenOutlined />,
                  label: t("reports.actions.move"),
                  onClick: () => onMove(record)
                }
              ]
            : []),
          ...(owned
            ? [
                {
                  key: "share",
                  icon: <ShareAltOutlined />,
                  label: t("reports.actions.share"),
                  onClick: () => onShare(record)
                }
              ]
            : []),
          {
            key: "duplicate",
            icon: <CopyOutlined />,
            label: t("reports.actions.duplicate"),
            onClick: () => onDuplicate(record)
          },
          ...(owned
            ? [
                { type: "divider" as const },
                {
                  key: "delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  label: t("reports.actions.delete"),
                  onClick: () => onDelete(record)
                }
              ]
            : [])
        ]
      },
    [t, onOpen, onEdit, onSettings, onMove, onShare, onDuplicate, onDelete]
  )

  const columns = useMemo<ColumnsType<ReportListItem>>(
    () => [
      {
        key: "favorite",
        width: 40,
        align: "center",
        render: (_value, record) => (
          <Button
            type="text"
            size="small"
            aria-pressed={record.is_favorite}
            aria-label={
              record.is_favorite
                ? t("reports.actions.unfavorite")
                : t("reports.actions.favorite")
            }
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(record)
            }}
            icon={
              record.is_favorite ? (
                <StarFilled style={{ color: semantic.star }} />
              ) : (
                <StarOutlined style={{ color: neutral.faint }} />
              )
            }
          />
        )
      },
      {
        title: t("reports.columns.name"),
        dataIndex: "name",
        key: "name",
        width: 220,
        align: "left",
        sorter: (a, b) => byText(a.name, b.name),
        render: (_value: string, record) => (
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-lg text-base"
              style={{ background: "rgba(42,125,140,.10)", color: brand.teal }}
            >
              <FileTextOutlined />
            </span>
            <div className="flex min-w-0 flex-col">
              <Text strong ellipsis={{ tooltip: record.name }}>
                {record.name}
              </Text>
              {record.description ? (
                <Text
                  type="secondary"
                  className="text-xs"
                  ellipsis={{ tooltip: record.description }}
                >
                  {record.description}
                </Text>
              ) : null}
            </div>
          </div>
        )
      },
      ...(showFolder
        ? [
            {
              title: t("reports.columns.folder"),
              key: "folder",
              dataIndex: "folder",
              width: 120,
              align: "left" as const,
              sorter: (a: ReportListItem, b: ReportListItem) =>
                byText(a.folder || "", b.folder || ""),
              render: (folder: string) =>
                folder ? (
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <FolderOutlined
                      aria-hidden
                      style={{ color: semantic.star }}
                    />
                    <Text ellipsis={{ tooltip: folder }}>{folder}</Text>
                  </span>
                ) : (
                  <Text type="secondary">{t("reports.folders.unfiled")}</Text>
                )
            }
          ]
        : []),
      {
        title: t("reports.columns.owner"),
        key: "owner",
        width: 130,
        align: "left",
        sorter: (a, b) =>
          byText(getEntityName(a.owner), getEntityName(b.owner)),
        render: (_value, record) => getEntityName(record.owner) || empty
      },
      {
        title: t("reports.columns.sharing"),
        dataIndex: "visibility",
        key: "visibility",
        width: 140,
        align: "left",
        render: (value: ReportVisibility, record) => {
          const level = accessLevelOf(record)
          return (
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden style={{ color: neutral.muted }}>
                  {VISIBILITY_ICON[value] ?? <TeamOutlined />}
                </span>
                {t(`reports.visibility.${value}`, { defaultValue: value })}
              </span>
              {level !== "owner" && (
                <Tooltip title={t("reports.sharing.sharedWithYouHint")}>
                  <Tag className="m-0" color={ACCESS_COLOR[level]}>
                    {t(`reports.sharing.yourAccess.${level}`)}
                  </Tag>
                </Tooltip>
              )}
            </span>
          )
        }
      },
      {
        title: t("reports.columns.access"),
        key: "access",
        width: 110,
        render: (_value, record) => {
          const level = accessLevelOf(record)
          return (
            <Tag color={ACCESS_COLOR[level]}>
              {t(`reports.access.${level}`)}
            </Tag>
          )
        }
      },
      {
        title: t("reports.columns.widgets"),
        dataIndex: "widget_count",
        key: "widget_count",
        width: 100,
        align: "center",
        sorter: (a, b) => a.widget_count - b.widget_count
      },
      {
        title: t("reports.columns.updated"),
        dataIndex: "updated_at",
        key: "updated_at",
        width: 150,
        align: "left",
        sorter: (a, b) => byText(a.updated_at, b.updated_at),
        render: (value: string) => (
          <span className="whitespace-nowrap">
            {formatDate(value) || empty}
          </span>
        )
      },
      {
        key: "actions",
        width: 56,
        align: "center",
        // Pinned so the row's actions stay in reach however narrow the table.
        fixed: "right",
        render: (_value, record) => (
          // Dropdown menus portal out of the row, but React re-bubbles their
          // clicks through the component tree; stop them here so a menu action
          // never also triggers the row's open handler.
          <span onClick={(e) => e.stopPropagation()}>
            <Dropdown menu={{ items: rowMenu(record) }} trigger={["click"]}>
              <Button
                type="text"
                size="small"
                aria-label={t("reports.actions.menuFor", { name: record.name })}
                icon={<MoreOutlined />}
              />
            </Dropdown>
          </span>
        )
      }
    ],
    [t, formatDate, onToggleFavorite, rowMenu, showFolder]
  )

  return (
    <EntityTable<ReportListItem>
      surface={TABLE_SURFACES.reports}
      tab={DEFAULT_TAB}
      rowKey="id"
      columns={columns}
      dataSource={reports}
      loading={loading}
      rowSelection={rowSelection}
      defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
      hideConfigButton
      configControl={configControl}
      fitScrollX
      withOutPagination
      pagination={false}
      onRow={(record) => ({
        onClick: () => onOpen(record)
      })}
      rowClassName="cursor-pointer"
    />
  )
}
